import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';
import { sendDisputeEmails } from '../../lib/emails';

const AlertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const CheckIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const DisputeLocationModal = ({ isOpen, onClose, locationId, locationData, currentOwnerId }) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { activeProfile } = useProfile();

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const [form, setForm] = useState({
    fullName: '',
    role: '',
    contactEmail: '',
    contactPhone: '',
    evidenceDescription: '',
  });

  useEffect(() => {
    if (isOpen) {
      setForm({ fullName: '', role: '', contactEmail: '', contactPhone: '', evidenceDescription: '' });
      setUploadedFiles([]);
      setSuccess(false);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canSubmit = form.fullName.trim() && form.role && form.contactEmail.trim() && form.evidenceDescription.trim();

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    const newUrls = [];

    for (const file of files) {
      const ext = file.name.split('.').pop();
      const path = `${activeProfile.id}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

      const { data, error } = await supabase.storage
        .from('dispute-documents')
        .upload(path, file);

      if (!error && data) {
        const { data: urlData } = supabase.storage
          .from('dispute-documents')
          .getPublicUrl(data.path);
        newUrls.push(urlData.publicUrl);
      }
    }

    setUploadedFiles(prev => [...prev, ...newUrls]);
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('location_disputes')
        .insert({
          location_id: locationId,
          disputant_id: activeProfile.id,
          current_owner_id: currentOwnerId || null,
          full_name: form.fullName.trim(),
          role_at_business: form.role,
          contact_email: form.contactEmail.trim(),
          contact_phone: form.contactPhone.trim() || null,
          evidence_description: form.evidenceDescription.trim(),
          document_urls: uploadedFiles,
        });

      if (error) throw error;

      // Send notification emails (non-blocking)
      sendDisputeEmails(activeProfile.id, currentOwnerId, locationId).catch(err =>
        console.error('[Email] Dispute emails failed:', err)
      );

      setSuccess(true);
    } catch (err) {
      console.error('Error submitting dispute:', err);
      alert('Failed to submit dispute. Please try again.');
      setSubmitting(false);
    }
  };

  const styles = {
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 999,
    },
    sheet: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      maxHeight: '90vh',
      backgroundColor: theme.bgSheet,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideUp 0.3s ease-out',
    },
    handle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.border,
      margin: '12px auto 0',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px 8px',
    },
    headerTitle: {
      fontSize: 17,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      fontSize: 15,
      color: theme.textMuted,
      cursor: 'pointer',
      padding: 8,
    },
    body: {
      flex: 1,
      overflowY: 'auto',
      padding: '0 16px 16px',
    },
    warningBanner: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: 12,
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      borderRadius: 10,
      marginBottom: 20,
      color: '#f59e0b',
      fontSize: 13,
      lineHeight: 1.5,
    },
    locationInfo: {
      padding: 12,
      backgroundColor: theme.bgCard,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      marginBottom: 20,
    },
    locationName: {
      fontSize: 16,
      fontWeight: 600,
      color: theme.text,
      margin: 0,
    },
    locationAddress: {
      fontSize: 13,
      color: theme.textSecondary,
      marginTop: 4,
    },
    label: {
      display: 'block',
      fontSize: 13,
      fontWeight: 600,
      color: theme.text,
      marginBottom: 6,
    },
    required: {
      color: '#ef4444',
    },
    input: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
      color: theme.text,
      fontSize: 15,
      boxSizing: 'border-box',
      outline: 'none',
    },
    select: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
      color: theme.text,
      fontSize: 15,
      boxSizing: 'border-box',
      outline: 'none',
      appearance: 'none',
    },
    textarea: {
      width: '100%',
      padding: 12,
      borderRadius: 10,
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
      color: theme.text,
      fontSize: 15,
      boxSizing: 'border-box',
      outline: 'none',
      minHeight: 100,
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    fieldGroup: {
      marginBottom: 16,
    },
    uploadArea: {
      border: `2px dashed ${theme.border}`,
      borderRadius: 10,
      padding: 20,
      textAlign: 'center',
      cursor: 'pointer',
      color: theme.textSecondary,
      fontSize: 14,
      marginBottom: 8,
    },
    fileList: {
      fontSize: 13,
      color: theme.textSecondary,
      marginBottom: 16,
    },
    fileName: {
      padding: '4px 0',
      color: theme.accent,
    },
    button: {
      width: '100%',
      padding: 14,
      borderRadius: 12,
      border: 'none',
      backgroundColor: '#ef4444',
      color: '#fff',
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    successContainer: {
      padding: '40px 16px',
      textAlign: 'center',
    },
    successCircle: {
      width: 64,
      height: 64,
      borderRadius: '50%',
      backgroundColor: 'rgba(34, 197, 94, 0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      color: '#22c55e',
    },
    successTitle: {
      fontSize: 20,
      fontWeight: 600,
      color: theme.text,
      margin: '0 0 12px',
    },
    successText: {
      fontSize: 14,
      color: theme.textSecondary,
      lineHeight: 1.5,
      margin: '0 0 8px',
    },
    doneButton: {
      width: '100%',
      padding: 14,
      borderRadius: 12,
      border: 'none',
      backgroundColor: theme.accent,
      color: '#fff',
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      marginTop: 20,
    },
  };

  return (
    <>
      <div style={styles.backdrop} onClick={success ? onClose : undefined} />
      <div style={styles.sheet}>
        <div style={styles.handle} />

        <div style={styles.header}>
          <h3 style={styles.headerTitle}>
            {success ? 'Dispute Submitted' : 'Dispute Ownership'}
          </h3>
          <button style={styles.closeBtn} onClick={onClose}>
            {success ? 'Done' : 'Cancel'}
          </button>
        </div>

        <div style={styles.body}>
          {success ? (
            <div style={styles.successContainer}>
              <div style={styles.successCircle}>
                <CheckIcon />
              </div>
              <h3 style={styles.successTitle}>Dispute Submitted</h3>
              <p style={styles.successText}>
                Our team will review your dispute and may request additional documentation. Both you and the current claimant will be notified of the outcome.
              </p>
              <p style={styles.successText}>
                This process typically takes 3-5 business days.
              </p>
              <button style={styles.doneButton} onClick={onClose}>
                Done
              </button>
            </div>
          ) : (
            <>
              <div style={styles.warningBanner}>
                <div style={{ flexShrink: 0, marginTop: 1 }}><AlertIcon /></div>
                <span>
                  This location has already been claimed by another user. If you believe you are the rightful owner, please provide evidence below.
                </span>
              </div>

              <div style={styles.locationInfo}>
                <p style={styles.locationName}>{locationData?.store_name}</p>
                <p style={styles.locationAddress}>
                  {locationData?.address && `${locationData.address}, `}
                  {locationData?.city}, {locationData?.state} {locationData?.zip_code}
                </p>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Full Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Your full legal name"
                  value={form.fullName}
                  onChange={(e) => setForm(prev => ({ ...prev, fullName: e.target.value }))}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Your Role <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.role}
                  onChange={(e) => setForm(prev => ({ ...prev, role: e.target.value }))}
                  style={styles.select}
                >
                  <option value="">Select your role</option>
                  <option value="Owner">Owner</option>
                  <option value="Manager">Manager</option>
                  <option value="Authorized Representative">Authorized Representative</option>
                </select>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Business Email <span style={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  placeholder="you@yourbusiness.com"
                  value={form.contactEmail}
                  onChange={(e) => setForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Business Phone</label>
                <input
                  type="tel"
                  placeholder="(555) 555-5555"
                  value={form.contactPhone}
                  onChange={(e) => setForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  Evidence Description <span style={styles.required}>*</span>
                </label>
                <textarea
                  placeholder="Describe your proof of ownership (e.g., business license details, years of operation, how you can verify identity...)"
                  value={form.evidenceDescription}
                  onChange={(e) => setForm(prev => ({ ...prev, evidenceDescription: e.target.value }))}
                  style={styles.textarea}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>Supporting Documents</label>
                <label style={styles.uploadArea}>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <div style={{ marginBottom: 8 }}><UploadIcon /></div>
                  {uploading ? 'Uploading...' : 'Tap to upload business license, utility bill, or other proof'}
                </label>
                {uploadedFiles.length > 0 && (
                  <div style={styles.fileList}>
                    {uploadedFiles.map((url, i) => (
                      <div key={i} style={styles.fileName}>
                        Document {i + 1} uploaded
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                style={{
                  ...styles.button,
                  ...(!canSubmit || uploading ? styles.buttonDisabled : {}),
                }}
                onClick={handleSubmit}
                disabled={!canSubmit || submitting || uploading}
              >
                <AlertIcon />
                {submitting ? 'Submitting...' : 'Submit Dispute'}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DisputeLocationModal;
